import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, CircleCheck, CircleX } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "@/api/client";
import { useCancelLeadImport, usePreviewLeadMapping, useStartLeadImport } from "@/api/mutations/lead-imports";
import { useLeadImportFields } from "@/api/queries/lead-imports";
import type { LeadImport, LeadImportPreview, LeadMapping } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { NativeSelect, NativeSelectOptGroup, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useStoreParam } from "@/hooks/use-store-context";
import { EMPTY, formatMiles, formatMoney, formatNumber, formatPhone } from "@/lib/formatters";
import { cn } from "@/lib/utils";

const GROUPS = [
  ["seller", "Seller"],
  ["vehicle", "Vehicle"],
  ["lead", "Lead"],
] as const;

/**
 * Step 2 of a file import: confirm which lead field each column (by header name) fills. The
 * server suggested a mapping; every change is previewed on the first rows before starting.
 */
export function MappingEditor({ imp, canEdit }: { imp: LeadImport; canEdit: boolean }) {
  const store = useStoreParam();
  const fields = useLeadImportFields();
  const [mapping, setMapping] = useState<LeadMapping>(imp.mapping);
  const [preview, setPreview] = useState<LeadImportPreview | null>(null);
  const previewer = usePreviewLeadMapping(imp.id);
  const start = useStartLeadImport(imp.id);
  const discard = useCancelLeadImport(imp.id);
  const navigate = useNavigate();
  const seq = useRef(0);

  // Re-preview whenever the mapping changes (latest request wins).
  const { mutate: requestPreview } = previewer;
  useEffect(() => {
    const n = ++seq.current;
    requestPreview(mapping, { onSuccess: (p) => n === seq.current && setPreview(p) });
  }, [mapping, requestPreview]);

  const usedBy = useMemo(() => {
    const m = new Map<string, string>();
    for (const [header, field] of Object.entries(mapping)) if (field) m.set(field, header);
    return m;
  }, [mapping]);
  const problems = preview?.problems ?? [];
  const mapped = Object.values(mapping).filter(Boolean).length;

  const begin = () =>
    start.mutate(
      { mapping },
      {
        onSuccess: () => toast.success("Import started", { description: "Leads are being added. You can leave this page." }),
        onError: (err) => toast.error("Import not started", { description: errorMessage(err) }),
      },
    );

  return (
    <div className="space-y-5">
      <section aria-labelledby="mapping-title" className="rounded-lg border">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
          <div>
            <h2 id="mapping-title" className="font-semibold text-sm">
              Match your columns
            </h2>
            <p className="text-muted-foreground text-xs">
              {mapped} of {imp.headers.length} columns imported. Unmatched columns are kept with each lead as extra details.
            </p>
          </div>
        </header>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-1/4 pl-4">Column in your file</TableHead>
              <TableHead>Example values</TableHead>
              <TableHead className="w-12" />
              <TableHead className="w-1/4 pr-4">Rogue field</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {imp.headers.map((header) => {
              const value = mapping[header] ?? "";
              const clash = value && usedBy.get(value) !== header;
              return (
                <TableRow key={header} data-testid="mapping-row">
                  <TableCell className="pl-4 font-medium">{header}</TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground text-xs">{preview?.samples[header]?.join(" · ") || EMPTY}</TableCell>
                  <TableCell>
                    <ArrowRight className={cn("size-3.5", value ? "text-foreground" : "text-muted-foreground/40")} aria-hidden />
                  </TableCell>
                  <TableCell className="pr-4">
                    <NativeSelect
                      aria-label={`Field for ${header}`}
                      value={value}
                      disabled={!canEdit}
                      aria-invalid={Boolean(clash)}
                      onChange={(e) => setMapping((m) => ({ ...m, [header]: e.target.value || null }))}
                      className="w-full"
                    >
                      <NativeSelectOption value="">Don't import</NativeSelectOption>
                      {GROUPS.map(([group, label]) => (
                        <NativeSelectOptGroup key={group} label={label}>
                          {(fields.data?.fields ?? [])
                            .filter((f) => f.group === group)
                            .map((f) => (
                              <NativeSelectOption key={f.key} value={f.key}>
                                {f.header}
                                {usedBy.has(f.key) && usedBy.get(f.key) !== header ? ` (used by “${usedBy.get(f.key)}”)` : ""}
                              </NativeSelectOption>
                            ))}
                        </NativeSelectOptGroup>
                      ))}
                    </NativeSelect>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </section>

      {problems.length ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      <section aria-labelledby="preview-title" className="rounded-lg border">
        <header className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
          <div>
            <h2 id="preview-title" className="font-semibold text-sm">
              Preview
            </h2>
            <p className="text-muted-foreground text-xs">How the first rows will be imported. Each lead needs a valid phone or email.</p>
          </div>
          {previewer.isPending ? <Spinner className="text-muted-foreground" /> : null}
        </header>
        {preview && !problems.length ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4">Row</TableHead>
                <TableHead>Seller</TableHead>
                <TableHead>Phone / email</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead className="text-right">Mileage</TableHead>
                <TableHead className="text-right">Asking</TableHead>
                <TableHead className="pr-4">Check</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.rows.map((r) => (
                <TableRow key={r.rowNumber} data-testid="preview-row">
                  <TableCell className="pl-4 text-muted-foreground tabular-nums">{r.rowNumber}</TableCell>
                  <TableCell>{r.lead?.name ?? EMPTY}</TableCell>
                  <TableCell className="text-xs">
                    {r.lead ? (
                      <>
                        {r.lead.phone ? <span className="block">{formatPhone(r.lead.phone)}</span> : null}
                        {r.lead.email ? <span className="block text-muted-foreground">{r.lead.email}</span> : null}
                      </>
                    ) : (
                      EMPTY
                    )}
                  </TableCell>
                  <TableCell>{r.lead?.vehicle ?? EMPTY}</TableCell>
                  <TableCell className="text-right">{r.lead?.mileage != null ? formatMiles(r.lead.mileage) : EMPTY}</TableCell>
                  <TableCell className="text-right">{r.lead?.askingPriceCents != null ? formatMoney(r.lead.askingPriceCents) : EMPTY}</TableCell>
                  <TableCell className="pr-4 text-xs">
                    <RowCheck lead={Boolean(r.lead)} issues={r.issues} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="px-4 py-6 text-muted-foreground text-sm">{problems.length ? "Fix the mapping to see a preview." : "Loading preview…"}</p>
        )}
        <p className="border-t px-4 py-2 text-muted-foreground text-xs">
          {formatNumber(imp.counts.total)} leads will be imported into {imp.storeName ?? "the store"}. Leads already in Rogue (same Lead ID, or a seller with an
          open opportunity) are reported as duplicates, never created twice.
        </p>
      </section>

      {canEdit ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="ghost"
            disabled={discard.isPending}
            onClick={() =>
              discard.mutate(undefined, {
                onSuccess: () => {
                  toast.success("Upload discarded");
                  void navigate({ to: "/$store/imports", params: { store } });
                },
                onError: (err) => toast.error("Not discarded", { description: errorMessage(err) }),
              })
            }
          >
            Discard upload
          </Button>
          <Button onClick={begin} disabled={start.isPending || !preview || problems.length > 0 || previewer.isPending}>
            {start.isPending ? <Spinner data-icon="inline-start" /> : null}
            Import {formatNumber(imp.counts.total)} leads
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RowCheck({ lead, issues }: { lead: boolean; issues: Array<{ message: string; level: string }> }) {
  if (!lead) {
    return (
      <span className="inline-flex items-start gap-1 text-danger">
        <CircleX className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        {issues.find((i) => i.level === "error")?.message ?? "Will be skipped"}
      </span>
    );
  }
  const warnings = issues.filter((i) => i.level === "warning");
  if (warnings.length) {
    return (
      <span className="inline-flex items-start gap-1 text-warning" title={warnings.map((w) => w.message).join("\n")}>
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        {warnings[0]?.message}
        {warnings.length > 1 ? ` (+${warnings.length - 1})` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-success">
      <CircleCheck className="size-3.5" aria-hidden />
      Ready
    </span>
  );
}
